import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/hooks/use-auth";
import { CartProvider } from "@/hooks/use-cart";
import { ThemeProvider } from "@/hooks/use-theme";
import { RootLayout } from "@/layouts/root-layout";

const HomePage = lazy(() =>
  import("@/pages/home").then((m) => ({ default: m.HomePage })),
);
const ShopPage = lazy(() =>
  import("@/pages/shop").then((m) => ({ default: m.ShopPage })),
);
const ProductPage = lazy(() =>
  import("@/pages/product").then((m) => ({ default: m.ProductPage })),
);
const LoginPage = lazy(() =>
  import("@/pages/login").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/pages/register").then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/forgot-password").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/reset-password").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const AccountLayout = lazy(() =>
  import("@/pages/account/layout").then((m) => ({ default: m.AccountLayout })),
);
const AccountOverviewPage = lazy(() =>
  import("@/pages/account/overview").then((m) => ({
    default: m.AccountOverviewPage,
  })),
);
const AccountOrdersPage = lazy(() =>
  import("@/pages/account/orders").then((m) => ({
    default: m.AccountOrdersPage,
  })),
);
const AccountOrderDetailPage = lazy(() =>
  import("@/pages/account/order-detail").then((m) => ({
    default: m.AccountOrderDetailPage,
  })),
);
const AccountWishlistPage = lazy(() =>
  import("@/pages/account/wishlist").then((m) => ({
    default: m.AccountWishlistPage,
  })),
);
const AccountAddressesPage = lazy(() =>
  import("@/pages/account/addresses").then((m) => ({
    default: m.AccountAddressesPage,
  })),
);
const CheckoutPage = lazy(() =>
  import("@/pages/checkout").then((m) => ({ default: m.CheckoutPage })),
);
const CheckoutConfirmationPage = lazy(() =>
  import("@/pages/checkout-confirmation").then((m) => ({
    default: m.CheckoutConfirmationPage,
  })),
);
const AdminLayout = lazy(() =>
  import("@/pages/admin/layout").then((m) => ({ default: m.AdminLayout })),
);
const AdminOverviewPage = lazy(() =>
  import("@/pages/admin/overview").then((m) => ({
    default: m.AdminOverviewPage,
  })),
);
const AdminPricesPage = lazy(() =>
  import("@/pages/admin/prices").then((m) => ({ default: m.AdminPricesPage })),
);
const AdminInventoryPage = lazy(() =>
  import("@/pages/admin/inventory").then((m) => ({
    default: m.AdminInventoryPage,
  })),
);
const AdminOrdersPage = lazy(() =>
  import("@/pages/admin/orders").then((m) => ({ default: m.AdminOrdersPage })),
);
const AdminOrderDetailPage = lazy(() =>
  import("@/pages/admin/order-detail").then((m) => ({
    default: m.AdminOrderDetailPage,
  })),
);
const AdminMarketplacesPage = lazy(() =>
  import("@/pages/admin/marketplaces").then((m) => ({
    default: m.AdminMarketplacesPage,
  })),
);
const AdminCouponsPage = lazy(() =>
  import("@/pages/admin/coupons").then((m) => ({
    default: m.AdminCouponsPage,
  })),
);
const AdminSettingsPage = lazy(() =>
  import("@/pages/admin/settings").then((m) => ({
    default: m.AdminSettingsPage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import("@/pages/policies").then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsPolicyPage = lazy(() =>
  import("@/pages/policies").then((m) => ({ default: m.TermsPolicyPage })),
);
const ShippingPolicyPage = lazy(() =>
  import("@/pages/policies").then((m) => ({ default: m.ShippingPolicyPage })),
);
const RefundPolicyPage = lazy(() =>
  import("@/pages/policies").then((m) => ({ default: m.RefundPolicyPage })),
);
const ReturnsPolicyPage = lazy(() =>
  import("@/pages/policies").then((m) => ({ default: m.ReturnsPolicyPage })),
);
const HomemadeChutneysGuidePage = lazy(() =>
  import("@/pages/guides/homemade-chutneys").then((m) => ({
    default: m.HomemadeChutneysGuidePage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function PageFallback() {
  return (
    <div className="px-5 py-20 text-center text-sm text-muted">Loading…</div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <CartProvider>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route element={<RootLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="shop" element={<ShopPage />} />
                  <Route path="products/:slug" element={<ProductPage />} />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="register" element={<RegisterPage />} />
                  <Route path="forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="reset-password" element={<ResetPasswordPage />} />
                  <Route path="account" element={<AccountLayout />}>
                    <Route index element={<AccountOverviewPage />} />
                    <Route path="orders" element={<AccountOrdersPage />} />
                    <Route
                      path="orders/:orderNumber"
                      element={<AccountOrderDetailPage />}
                    />
                    <Route path="wishlist" element={<AccountWishlistPage />} />
                    <Route path="addresses" element={<AccountAddressesPage />} />
                  </Route>
                  <Route path="checkout" element={<CheckoutPage />} />
                  <Route
                    path="checkout/confirmation"
                    element={<CheckoutConfirmationPage />}
                  />
                  <Route path="policies/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="policies/terms" element={<TermsPolicyPage />} />
                  <Route
                    path="policies/shipping"
                    element={<ShippingPolicyPage />}
                  />
                  <Route path="policies/refund" element={<RefundPolicyPage />} />
                  <Route
                    path="policies/returns"
                    element={<ReturnsPolicyPage />}
                  />
                  <Route
                    path="guides/homemade-chutneys"
                    element={<HomemadeChutneysGuidePage />}
                  />
                  <Route path="admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverviewPage />} />
                    <Route path="prices" element={<AdminPricesPage />} />
                    <Route path="inventory" element={<AdminInventoryPage />} />
                    <Route path="coupons" element={<AdminCouponsPage />} />
                    <Route path="orders" element={<AdminOrdersPage />} />
                    <Route
                      path="orders/:orderNumber"
                      element={<AdminOrderDetailPage />}
                    />
                    <Route
                      path="marketplaces"
                      element={<AdminMarketplacesPage />}
                    />
                    <Route path="settings" element={<AdminSettingsPage />} />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </CartProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);
}
